/**
 * API client + TypeScript types for UGC Studio.
 * Mirrors the shapes returned by /api/ugc-studio/*.
 */
import { apiRequest, apiRequestSSE } from "@/lib/queryClient";
import { stripImageMetadata } from "@/lib/strip-image-metadata";

export type UgcFormatId =
  | "ugc"
  | "tutorial"
  | "unboxing"
  | "hyper-motion"
  | "product-review"
  | "tv-spot"
  | "wild-card"
  | "ugc-virtual-try-on"
  | "pro-virtual-try-on";

export type AspectRatio = "9:16" | "16:9" | "1:1" | "3:4" | "4:5";
export type Resolution = "480p" | "720p" | "1080p";
export type Duration = 5 | 10 | 15;
export type VideoModel = "seedance-2.0";
export type Gender = "male" | "female" | "neutral";
export type ProductType = "product" | "app";
export type Platform = "desktop" | "mobile";
export type AvatarFilter = "all" | "pinned" | "my";
export type JobStatus = "queued" | "generating" | "completed" | "failed";

export interface UgcFormat {
  id: UgcFormatId;
  label: string;
  description: string;
  promptTemplate: string;
  userBriefHint: string;
  defaultAspectRatio: AspectRatio;
  defaultDuration: Duration;
  prefersImageReference: boolean;
}

export interface UgcAvatar {
  id: string;
  userId: string;
  name: string;
  gender: Gender | null;
  sourceType: "generated" | "uploaded";
  r2Key: string;
  prompt: string | null;
  modelUsed: string | null;
  isPinned: boolean;
  isPublic: boolean;
  createdAt: string;
  signedUrl: string;
  appearanceMeta?: {
    spokenLanguage?: string | null;
    nationality?: string | null;
    [key: string]: unknown;
  } | null;
}

export interface UgcProduct {
  id: string;
  userId: string;
  type: ProductType;
  name: string;
  description: string | null;
  sourceUrl: string | null;
  platform: Platform | null;
  imageR2Keys: string[];
  imageUrls: string[];
  createdAt: string;
}

export type AudioMode = "native" | "overlay" | "lipsync" | "voiceover";

export interface ScriptBeat {
  start: number;
  end: number;
  text: string;
}

export type Script = ScriptBeat[];

export interface UgcJob {
  id: string;
  userId: string;
  status: JobStatus;
  prompt: string;
  format: UgcFormatId;
  aspectRatio: AspectRatio;
  resolution: Resolution | null;
  durationSeconds: number;
  videoModel: VideoModel;
  avatarId: string | null;
  productId: string | null;
  outputR2Key: string | null;
  outputGraphicId: string | null;
  tokensCharged: number;
  progress: number;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  videoUrl?: string | null;
  script?: Script | null;
  audioMode?: AudioMode | null;
  voiceId?: string | null;
  audioR2Key?: string | null;
  seedanceR2Key?: string | null;
  /** Signed URL of the raw Seedance MP4 (only present when audio !== 'native'). */
  seedanceVideoUrl?: string | null;
  /** Signed URL of the TTS audio MP3 (only present when audio !== 'native'). */
  audioUrl?: string | null;
}

// ── API calls ───────────────────────────────────────────────

/** Mirrors MAX_UPLOAD_BYTES in server/modules/ugc-studio/routes.ts. */
export const MAX_AVATAR_UPLOAD_BYTES = 15 * 1024 * 1024;

/** Upload failure with a machine-readable code so UI can show a translated message. */
export class AvatarUploadError extends Error {
  constructor(
    readonly code: "rejected-by-proxy" | "api-error",
    message: string,
    readonly status?: number
  ) {
    super(message);
    this.name = "AvatarUploadError";
  }
}

/**
 * Infra przed Expressem (CloudFront/proxy) potrafi odrzucić duży upload stroną
 * HTML — wtedy `resp.json()` wybucha z "Unexpected token '<' ... is not valid
 * JSON". Parsujemy defensywnie i zwracamy czytelny błąd zamiast surowego HTML.
 */
async function parseUploadResponse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  const isHtml = text.trimStart().startsWith("<");
  if (isHtml) {
    throw new AvatarUploadError("rejected-by-proxy", `HTTP ${resp.status}`, resp.status);
  }
  if (!resp.ok) {
    let message = text || `HTTP ${resp.status}`;
    try {
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) message = parsed.message;
    } catch {
      // body nie był JSON-em — zostaje surowy tekst
    }
    throw new AvatarUploadError("api-error", message, resp.status);
  }
  return JSON.parse(text) as T;
}

export const ugcStudioApi = {
  listFormats: () =>
    apiRequest("GET", "/api/ugc-studio/formats") as Promise<{ formats: UgcFormat[] }>,

  listAvatars: (filter: AvatarFilter, gender?: Gender) => {
    const params = new URLSearchParams({ filter });
    if (gender) params.set("gender", gender);
    return apiRequest("GET", `/api/ugc-studio/avatars?${params.toString()}`) as Promise<{
      avatars: UgcAvatar[];
    }>;
  },

  // VP-125: streamed over SSE (heartbeats) so the long gpt-image-2 generation
  // never trips CloudFront's 504. Same response shape as the old JSON endpoint.
  generateAvatar: (body: {
    name: string;
    prompt: string;
    gender?: Gender;
    spokenLanguage?: string;
    nationality?: string;
  }) =>
    apiRequestSSE("POST", "/api/ugc-studio/avatars/generate", body, {
      timeoutMs: 180_000,
    }) as Promise<{ avatar: UgcAvatar; tokensRemaining: number }>,

  uploadAvatar: async (params: {
    name: string;
    gender?: Gender;
    spokenLanguage?: string;
    nationality?: string;
    file: File;
    token: string;
  }) => {
    const fd = new FormData();
    // strip metadanych XMP — WAF na prodzie blokuje obrazki z XML-em w środku
    fd.append("file", await stripImageMetadata(params.file), params.file.name);
    fd.append("name", params.name);
    if (params.gender) fd.append("gender", params.gender);
    if (params.spokenLanguage) fd.append("spokenLanguage", params.spokenLanguage);
    if (params.nationality) fd.append("nationality", params.nationality);
    const resp = await fetch("/api/ugc-studio/avatars/upload", {
      method: "POST",
      headers: { Authorization: `Bearer ${params.token}` },
      body: fd,
    });
    return parseUploadResponse<{ avatar: UgcAvatar }>(resp);
  },

  updateAvatar: (
    id: string,
    body: {
      name?: string;
      isPinned?: boolean;
      spokenLanguage?: string | null;
      nationality?: string | null;
    }
  ) => apiRequest("PATCH", `/api/ugc-studio/avatars/${id}`, body) as Promise<{ avatar: UgcAvatar }>,

  editAvatarLook: (id: string, editPrompt: string) =>
    apiRequest(
      "POST",
      `/api/ugc-studio/avatars/${id}/edit-look`,
      { editPrompt },
      { timeoutMs: 180_000 }
    ) as Promise<{ avatar: UgcAvatar; tokensRemaining: number }>,

  recreateSimilarAvatar: (id: string, name?: string) =>
    apiRequest("POST", `/api/ugc-studio/avatars/${id}/recreate-similar`, name ? { name } : {}, {
      timeoutMs: 180_000,
    }) as Promise<{ avatar: UgcAvatar; tokensRemaining: number }>,

  deleteAvatar: (id: string) => apiRequest("DELETE", `/api/ugc-studio/avatars/${id}`),

  listProducts: (type?: ProductType) => {
    const qs = type ? `?type=${type}` : "";
    return apiRequest("GET", `/api/ugc-studio/products${qs}`) as Promise<{
      products: UgcProduct[];
    }>;
  },

  scrapeProduct: (url: string, type: ProductType) =>
    apiRequest("POST", "/api/ugc-studio/products/scrape", { url, type }) as Promise<{
      preview: { name: string; description: string; imageUrls: string[] };
      type: ProductType;
    }>,

  scrapeAndSaveProduct: (url: string, type: ProductType, platform?: Platform | null) =>
    apiRequest("POST", "/api/ugc-studio/products/scrape-and-save", {
      url,
      type,
      platform,
    }) as Promise<{ product: UgcProduct }>,

  createProduct: (body: {
    type: ProductType;
    name: string;
    description?: string;
    sourceUrl?: string | null;
    platform?: Platform | null;
    imageR2Keys?: string[];
  }) => apiRequest("POST", "/api/ugc-studio/products", body) as Promise<{ product: UgcProduct }>,

  deleteProduct: (id: string) => apiRequest("DELETE", `/api/ugc-studio/products/${id}`),

  uploadProductImages: async (params: { productId: string; files: File[]; token: string }) => {
    const fd = new FormData();
    params.files.forEach((f) => fd.append("files", f));
    const resp = await fetch(`/api/ugc-studio/products/${params.productId}/images`, {
      method: "POST",
      headers: { Authorization: `Bearer ${params.token}` },
      body: fd,
    });
    if (!resp.ok) throw new Error(await resp.text());
    return (await resp.json()) as { product: UgcProduct };
  },

  createJob: (body: {
    prompt: string;
    format: UgcFormatId;
    aspectRatio: AspectRatio;
    resolution: Resolution;
    durationSeconds: Duration;
    videoModel: VideoModel;
    avatarId?: string | null;
    productId?: string | null;
    /** Override the device (phone/laptop) shown for an app product. */
    deviceOverride?: Platform | null;
    script?: Script;
    audioMode?: AudioMode;
    voiceId?: string;
  }) => apiRequest("POST", "/api/ugc-studio/jobs", body) as Promise<{ job: UgcJob }>,

  previewScript: (body: {
    format: UgcFormatId;
    durationSeconds: number;
    avatarId?: string | null;
    productId?: string | null;
    userBrief?: string;
    language?: string;
  }) =>
    apiRequest("POST", "/api/ugc-studio/script/preview", body, { timeoutMs: 60_000 }) as Promise<{
      script: Script;
    }>,

  remixAudio: (jobId: string, body: { script: Script; audioMode: AudioMode; voiceId?: string }) =>
    apiRequest("POST", `/api/ugc-studio/jobs/${jobId}/remix-audio`, body, {
      timeoutMs: 180_000,
    }) as Promise<{
      graphicId: string;
      r2Key: string;
      videoUrl: string | null;
    }>,

  previewVoice: (body: { avatarId?: string; voiceId?: string }) =>
    apiRequest("POST", "/api/ugc-studio/voices/preview", body) as Promise<{
      voiceId: string;
      previewUrl: string;
    }>,

  listVoices: () =>
    apiRequest("GET", "/api/ugc-studio/voices") as Promise<{ voices: CustomVoice[] }>,

  // NOTE: Tworzenie głosów (designVoice/saveDesignedVoice) zostało usunięte —
  // konto ElevenLabs ma twardy limit własnych głosów. Picker korzysta wyłącznie
  // z listVoices/deleteVoice na wcześniej zapisanych głosach.
  deleteVoice: (id: string) => apiRequest("DELETE", `/api/ugc-studio/voices/${id}`),

  getJob: (id: string) =>
    apiRequest("GET", `/api/ugc-studio/jobs/${id}`) as Promise<{ job: UgcJob }>,

  listJobs: () => apiRequest("GET", "/api/ugc-studio/jobs") as Promise<{ jobs: UgcJob[] }>,
};

export interface CustomVoice {
  id: string;
  name: string;
  voiceId: string;
  prompt: string;
  language: string | null;
  samplePreviewKey: string | null;
  createdAt: string;
}
